import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import api, { API_BASE_URL } from '../config/api';

function BlogDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [categories, setCategories] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [allBlogs, setAllBlogs] = useState([]);
  
  useEffect(() => {
    fetchBlogDetail();
    fetchCategories();
    fetchAllBlogs();
  }, [id]);

  // Fetch recent posts after allBlogs is loaded
  useEffect(() => {
    const loadRecentPosts = async () => {
      try {
        // Fetch more posts to ensure we have enough after filtering
        const response = await api.get('/blogs/recent/5');
        if (response.data.success) {
          // Filter out current post from recent posts
          const filtered = response.data.data.filter(p => p.id !== parseInt(id));
          setRecentPosts(filtered.slice(0, 3));
        } else {
          // If API fails, use allBlogs as fallback
          if (allBlogs.length > 0) {
            const filtered = allBlogs.filter(p => p.id !== parseInt(id));
            setRecentPosts(filtered.slice(0, 3));
          }
        }
      } catch (error) {
        console.error('Error fetching recent posts:', error);
        // Fallback to allBlogs if API fails
        if (allBlogs.length > 0) {
          const filtered = allBlogs.filter(p => p.id !== parseInt(id));
          setRecentPosts(filtered.slice(0, 3));
        }
      }
    };

    if (allBlogs.length > 0 || id) {
      loadRecentPosts();
    }
  }, [allBlogs, id]);

  const fetchBlogDetail = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/blogs/${id}`);
      if (response.data.success) {
        setPost(response.data.data);
      } else {
        setPost(null);
      }
    } catch (error) {
      console.error('Error fetching blog detail:', error);
      setPost(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllBlogs = async () => {
    try {
      const response = await api.get('/blogs', { params: { per_page: 100 } });
      if (response.data.success) {
        setAllBlogs(response.data.data);
        // Calculate category counts from all blogs
        const counts = {};
        response.data.data.forEach(blog => {
          if (blog.category) {
            counts[blog.category] = (counts[blog.category] || 0) + 1;
          }
        });
        setCategoryCounts(counts);
      }
    } catch (error) {
      console.error('Error fetching all blogs:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/blogs/categories/list');
      if (response.data.success) {
        setCategories(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories(['CRM Software', 'CRM Solutions', 'Business Management']);
    }
  };


  const handleSearch = (e) => {
    e.preventDefault();
    navigate(`/blog?search=${encodeURIComponent(searchQuery)}`);
  };

  const handleCategoryClick = (category) => {
    navigate(`/blog?category=${encodeURIComponent(category)}`);
  };

  const handleShowAll = () => {
    navigate('/blog');
  };

  // Function to process HTML content and fix image URLs
  const processContentImages = (htmlContent) => {
    if (!htmlContent) return '';
    
    // Get backend base URL (remove /api from API_BASE_URL)
    let backendBaseUrl = API_BASE_URL.replace('/api', '');
    
    // If API_BASE_URL doesn't contain /api, use it as is but ensure it has protocol
    if (backendBaseUrl === API_BASE_URL) {
      // Try to extract base URL from API_BASE_URL
      const urlMatch = API_BASE_URL.match(/^(https?:\/\/[^\/]+)/);
      if (urlMatch) {
        backendBaseUrl = urlMatch[1];
      } else {
        // Fallback to default backend URL
        backendBaseUrl = 'http://localhost:8000';
      }
    }
    
    // Process image src attributes
    // Match src="/upload/blog/..." or src='/upload/blog/...' or src="upload/blog/..." (without leading slash)
    // Also handle cases with style attributes and other attributes
    let processedContent = htmlContent.replace(
      /src=(["'])([^"']*\/upload\/blog\/[^"']*)\1/gi,
      (match, quote, imagePath) => {
        // If it's already a full URL, don't change it
        if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
          return match;
        }
        // Ensure path starts with /
        const normalizedPath = imagePath.startsWith('/') ? imagePath : '/' + imagePath;
        // Convert relative path to absolute URL
        return `src=${quote}${backendBaseUrl}${normalizedPath}${quote}`;
      }
    );
    
    return processedContent;
  };

  // Fallback blog posts data - in a real app, this would come from an API
  const blogPosts = {
    1: {
      id: 1,
      title: "CRM Software in Malaysia",
      image: "/my/cb1.png",
      excerpt: "Our CRM Software (Customer Relationship Management) will help you turn conversations into conversions at every stage of a customer's journey.",
      content: `
        <p>Customer Relationship Management (CRM) software has become an essential tool for businesses in Malaysia looking to streamline their operations, improve customer relationships, and drive growth. In today's competitive market, having a robust CRM system can make all the difference between success and stagnation.</p>
        
        <h2>What is CRM Software?</h2>
        <p>CRM Software is a comprehensive platform designed to help businesses manage their interactions with current and potential customers. It centralizes customer data, tracks communication history, manages sales pipelines, and automates various business processes.</p>
        
        <h2>Benefits of CRM Software in Malaysia</h2>
        <p>For Malaysian businesses, CRM software offers numerous advantages:</p>
        <ul>
          <li><strong>Improved Customer Relationships:</strong> Track every interaction with your customers, from initial contact to post-sale support.</li>
          <li><strong>Enhanced Sales Performance:</strong> Manage your sales pipeline effectively and identify opportunities for growth.</li>
          <li><strong>Better Team Collaboration:</strong> Share customer information across departments for seamless communication.</li>
          <li><strong>Data-Driven Decisions:</strong> Access comprehensive reports and analytics to make informed business decisions.</li>
          <li><strong>Automated Workflows:</strong> Reduce manual tasks and focus on what matters most - building relationships.</li>
        </ul>
        
        <h2>Why Choose Acuit CRM?</h2>
        <p>Acuit CRM is a powerful, self-hosted CRM solution designed specifically for Malaysian businesses. With features like lead management, project tracking, invoicing, and customer support, Acuit CRM provides everything you need to manage your business effectively.</p>
        
        <p>Our CRM software helps you turn conversations into conversions at every stage of a customer's journey. Whether you're a small startup or a growing enterprise, Acuit CRM scales with your business needs.</p>
        
        <h2>Get Started Today</h2>
        <p>Ready to transform your customer relationships? Contact us today to learn more about how Acuit CRM can help your business grow.</p>
      `,
      author: "Acuit CRM Team",
      date: "January 15, 2024",
      category: "CRM Software"
    },
    2: {
      id: 2,
      title: "CRM Software in Kuala Lumpur | Best CRM ERP Software",
      image: "/my/cb2.png",
      excerpt: "Find the best CRM Software Company in Kuala Lumpur. Plenitude IT is a 100% web-based Customer Relationship Management System that helps businesses to enhance productivity & automate business processes.",
      content: `
        <p>Kuala Lumpur, as Malaysia's economic hub, is home to thousands of businesses seeking efficient ways to manage their customer relationships and operations. Finding the right CRM software in Kuala Lumpur can significantly impact your business success.</p>
        
        <h2>Why Kuala Lumpur Businesses Need CRM Software</h2>
        <p>With the rapid digital transformation in Malaysia's capital, businesses in Kuala Lumpur require modern solutions to stay competitive. CRM software provides the tools needed to:</p>
        <ul>
          <li>Manage growing customer bases efficiently</li>
          <li>Automate repetitive business processes</li>
          <li>Improve team productivity and collaboration</li>
          <li>Make data-driven business decisions</li>
          <li>Enhance customer satisfaction and retention</li>
        </ul>
        
        <h2>Features of the Best CRM ERP Software</h2>
        <p>The best CRM ERP software combines customer relationship management with enterprise resource planning capabilities. Key features include:</p>
        <ul>
          <li><strong>Lead Management:</strong> Track and manage leads from initial contact to conversion</li>
          <li><strong>Sales Pipeline:</strong> Visualize your sales process and identify bottlenecks</li>
          <li><strong>Project Management:</strong> Manage projects, tasks, and team collaboration</li>
          <li><strong>Invoicing & Billing:</strong> Create invoices, track payments, and manage finances</li>
          <li><strong>Customer Support:</strong> Handle customer inquiries and support tickets efficiently</li>
          <li><strong>Reporting & Analytics:</strong> Generate insights from your business data</li>
        </ul>
        
        <h2>Acuit CRM: Your Partner in Kuala Lumpur</h2>
        <p>Acuit CRM is a 100% web-based Customer Relationship Management System designed to help businesses in Kuala Lumpur enhance productivity and automate business processes. Our self-hosted solution gives you complete control over your data while providing all the features you need to succeed.</p>
        
        <p>Whether you're a startup in Bangsar or an established enterprise in KLCC, Acuit CRM scales with your business needs and provides the flexibility to customize according to your industry requirements.</p>
      `,
      author: "Acuit CRM Team",
      date: "January 20, 2024",
      category: "CRM Software"
    },
    3: {
      id: 3,
      title: "Why Choose Self-hosted CRM Solutions",
      image: "/my/cb3.png",
      excerpt: "Self-hosted CRM solutions offer complete control over your data, enhanced security, and no recurring subscription fees. Learn why Acuit CRM is the perfect choice for your business.",
      content: `
        <p>In an era where data privacy and cost-effectiveness are paramount, self-hosted CRM solutions have become increasingly popular among businesses of all sizes. Unlike cloud-based SaaS solutions, self-hosted CRM gives you complete control over your data and infrastructure.</p>
        
        <h2>What is Self-hosted CRM?</h2>
        <p>Self-hosted CRM is a Customer Relationship Management system that you install and run on your own servers or hosting environment. This means you have full ownership and control over your data, without relying on third-party cloud services.</p>
        
        <h2>Key Advantages of Self-hosted CRM</h2>
        
        <h3>1. Complete Data Ownership</h3>
        <p>With self-hosted CRM, your customer data stays on your servers. You have complete control over who accesses it, how it's stored, and where it's located. This is crucial for businesses handling sensitive information or operating in industries with strict data regulations.</p>
        
        <h3>2. Enhanced Security</h3>
        <p>Self-hosted solutions allow you to implement your own security measures, firewalls, and access controls. You're not sharing infrastructure with other companies, reducing the risk of data breaches.</p>
        
        <h3>3. No Recurring Subscription Fees</h3>
        <p>Most self-hosted CRM solutions, including Acuit CRM, offer a one-time purchase model. This means no monthly or annual subscription fees - you pay once and own the software forever. This is particularly beneficial for SMEs and freelancers who want premium features without ongoing costs.</p>
        
        <h3>4. Customization & Flexibility</h3>
        <p>Self-hosted CRM solutions are highly customizable. You can modify the code, add custom fields, integrate with your existing systems, and adapt the CRM to your specific business workflows.</p>
        
        <h3>5. No Vendor Lock-in</h3>
        <p>With self-hosted solutions, you're not tied to a specific vendor. You can migrate your data, modify the system, or switch hosting providers without losing access to your CRM.</p>
        
        <h3>6. Better Performance</h3>
        <p>Since the CRM runs on your own servers, you can optimize performance based on your specific needs. You're not sharing resources with other companies, ensuring consistent performance.</p>
        
        <h2>Why Acuit CRM is the Perfect Self-hosted Solution</h2>
        <p>Acuit CRM combines the benefits of self-hosted solutions with modern features and ease of use. Our CRM includes:</p>
        <ul>
          <li>17 comprehensive features covering all aspects of business management</li>
          <li>One-time purchase with lifetime updates</li>
          <li>Full source code access for customization</li>
          <li>GDPR-ready features for data protection</li>
          <li>Unlimited users, clients, and projects</li>
          <li>Comprehensive documentation and support</li>
        </ul>
        
        <h2>Is Self-hosted CRM Right for You?</h2>
        <p>Self-hosted CRM is ideal for:</p>
        <ul>
          <li>Businesses that prioritize data privacy and security</li>
          <li>Companies with specific customization needs</li>
          <li>Organizations looking to avoid recurring subscription fees</li>
          <li>Businesses that want complete control over their CRM system</li>
          <li>Companies with existing IT infrastructure and technical expertise</li>
        </ul>
        
        <h2>Get Started with Acuit CRM</h2>
        <p>Ready to experience the benefits of self-hosted CRM? Acuit CRM offers a cost-effective, feature-rich solution that gives you complete control over your customer relationship management. Contact us today to learn more or request a free demo.</p>
      `,
      author: "Acuit CRM Team",
      date: "January 25, 2024",
      category: "CRM Solutions"
    }
  };

  if (loading) {
    return (
      <div className="page-wrapper">
        <section className="page-banner bg-blue rel z-1" style={{backgroundImage: 'url(/assets/images/background/banner-bg.png)'}}>
          <div className="container">
            <div className="banner-inner">
              <h1 className="page-title">Loading...</h1>
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item"><Link to="/">Home</Link></li>
                  <li className="breadcrumb-item"><Link to="/blog">Blog</Link></li>
                  <li className="breadcrumb-item active">Loading</li>
                </ol>
              </nav>
            </div>
          </div>
        </section>
        <section className="blog-detail-section py-120 rpy-100">
          <div className="container">
            <div className="row">
              <div className="col-lg-12 text-center">
                <p>Loading blog post...</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Fallback to hardcoded data if API fails and post is null
  const fallbackPost = blogPosts[parseInt(id)];
  const currentPost = post || fallbackPost;

  if (!currentPost) {
    return (
      <div className="page-wrapper">
        <section className="page-banner bg-blue rel z-1" style={{backgroundImage: 'url(/assets/images/background/banner-bg.png)'}}>
          <div className="container">
            <div className="banner-inner">
              <h1 className="page-title">Blog Post Not Found</h1>
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item"><Link to="/">Home</Link></li>
                  <li className="breadcrumb-item"><Link to="/blog">Blog</Link></li>
                  <li className="breadcrumb-item active">Not Found</li>
                </ol>
              </nav>
            </div>
          </div>
        </section>
        <section className="blog-detail-section py-120 rpy-100">
          <div className="container">
            <div className="row">
              <div className="col-lg-12 text-center">
                <h2>Blog post not found</h2>
                <p>The blog post you're looking for doesn't exist.</p>
                <Link to="/blog" className="theme-btn">Back to Blog</Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <>
      <section className="page-banner bg-blue rel z-1" style={{backgroundImage: 'url(/assets/images/background/banner-bg.png)'}}>
        <div className="container">
          <div className="banner-inner">
            <h1 className="page-title wow fadeInUp delay-0-2s">{currentPost.title}</h1>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb wow fadeInUp delay-0-4s">
                <li className="breadcrumb-item"><Link to="/">Home</Link></li>
                <li className="breadcrumb-item"><Link to="/blog">Blog</Link></li>
                <li className="breadcrumb-item active">{currentPost.category || 'Blog'}</li>
              </ol>
            </nav>
          </div>
        </div>
        <img className="dots-shape" src="/assets/images/shapes/white-dots-two.png" alt="Shape" />
        <img className="tringle-shape slideLeftRight" src="/assets/images/shapes/white-tringle.png" alt="Shape" />
        <img className="close-shape" src="/assets/images/shapes/white-close.png" alt="Shape" />
        <img src="/assets/images/newsletter/circle.png" alt="shape" className="banner-circle slideUpRight" />
        <img className="dots-shape-three slideUpDown delay-1-5s" src="/assets/images/shapes/white-dots-three.png" alt="Shape" />
      </section>

      <section className="blog-detail-section py-120 rpy-100">
        <div className="container">
          <div className="row">
            <div className="col-lg-8">
              <article className="blog-detail-content">
                 <div className="blog-detail-image mb-40">
                   <img 
                     src={currentPost.featured_image || currentPost.image || "/my/cb1.png"} 
                     alt={currentPost.title} 
                     style={{width: '100%', borderRadius: '12px'}} 
                   />
                 </div>
                 
                 <h1 className="blog-detail-title mb-30">{currentPost.title}</h1>
                 
                 {/* Blog Meta Information */}
                 <div className="blog-meta-info mb-30" style={{
                   display: 'flex',
                   flexWrap: 'wrap',
                   gap: '20px',
                   alignItems: 'center',
                   padding: '15px 0',
                   borderTop: '1px solid #e5e7eb',
                   borderBottom: '1px solid #e5e7eb',
                   color: '#6b7280',
                   fontSize: '14px'
                 }}>
                   <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                     <i className="far fa-calendar-alt" style={{ color: '#4a90e2' }}></i>
                     <span>
                       {currentPost.published_at_formatted || currentPost.date || currentPost.created_at_formatted || 'N/A'}
                     </span>
                   </div>
                   <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                     <i className="fas fa-user" style={{ color: '#4a90e2' }}></i>
                     <span>{currentPost.author || 'Admin'}</span>
                   </div>
                   <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                     <i className="fas fa-tag" style={{ color: '#4a90e2' }}></i>
                     <span>{currentPost.category || 'Uncategorized'}</span>
                   </div>
                 </div>
                
                {/* Display main content */}
                {(currentPost.content && currentPost.content.trim() !== '') ? (
                  <div 
                    className="blog-detail-text" 
                    dangerouslySetInnerHTML={{ 
                      __html: processContentImages(currentPost.content)
                    }}
                  />
                ) : currentPost.short_description ? (
                  <div 
                    className="blog-detail-text"
                    dangerouslySetInnerHTML={{
                      __html: processContentImages(currentPost.short_description.replace(/\n/g, '<br />'))
                    }}
                  />
                ) : currentPost.excerpt ? (
                <div 
                  className="blog-detail-text" 
                    dangerouslySetInnerHTML={{
                      __html: processContentImages(currentPost.excerpt.replace(/\n/g, '<br />'))
                    }}
                />
                ) : (
                  <div className="blog-detail-text">
                    <p>No content available for this blog post.</p>
                  </div>
                )}
              </article>

              <div className="blog-navigation mt-50">
                <Link to="/blog" className="theme-btn">
                  <i className="fas fa-arrow-left"></i> Back to Blog
                </Link>
              </div>
            </div>

            <div className="col-lg-4 col-md-8">
              <div className="blog-sidebar">
                <div className="widget search-widget wow fadeInUp delay-0-2s">
                  <h3 className="widget-title">Search</h3>
                  <form onSubmit={handleSearch}>
                    <input 
                      type="search" 
                      placeholder="Keywords" 
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      required 
                    />
                    <button type="submit"><i className="fas fa-search"></i></button>
                  </form>
                            </div>
                <div className="widget category-widget wow fadeInUp delay-0-4s">
                  <h3 className="widget-title">Category</h3>
                  <ul>
                    <li>
                      <a 
                        href="#" 
                        onClick={(e) => {
                          e.preventDefault();
                          handleShowAll();
                        }}
                      >
                        All
                      </a>
                      <span>({allBlogs.length || 0})</span>
                    </li>
                    {categories.map((category, index) => (
                      <li key={index}>
                        <a 
                          href="#" 
                          onClick={(e) => {
                            e.preventDefault();
                            handleCategoryClick(category);
                          }}
                        >
                          {category}
                        </a>
                        <span>({categoryCounts[category] || 0})</span>
                        </li>
                      ))}
                    {categories.length === 0 && (
                      <>
                        <li><a href="#" onClick={(e) => { e.preventDefault(); handleCategoryClick('CRM Software'); }}>CRM Software</a> <span>(2)</span></li>
                        <li><a href="#" onClick={(e) => { e.preventDefault(); handleCategoryClick('CRM Solutions'); }}>CRM Solutions</a> <span>(1)</span></li>
                      </>
                    )}
                  </ul>
                </div>
                <div className="widget news-widget wow fadeInUp delay-0-2s">
                  <h3 className="widget-title">Recent News</h3>
                  <div className="news-widget-wrap">
                    {(() => {
                      const postsToShow = recentPosts.length > 0 
                        ? recentPosts 
                        : allBlogs.filter(p => p.id !== parseInt(id)).slice(0, 3);
                      
                      if (postsToShow.length > 0) {
                        return postsToShow.map((recentPost) => (
                          <div key={recentPost.id} className="news-widget-item">
                            <img 
                              src={recentPost.featured_image || recentPost.image || "/my/cb1.png"} 
                              alt={recentPost.title || "News"}
                            />
                            <div className="content">
                              <h5>
                                <Link to={`/blog/${recentPost.id}`}>{recentPost.title}</Link>
                              </h5>
                              <span className="date">
                                <i className="far fa-calendar-alt"></i>
                                <Link to={`/blog/${recentPost.id}`}>
                                  {recentPost.published_at_formatted || recentPost.created_at_formatted || 'N/A'}
                                </Link>
                              </span>
                            </div>
                          </div>
                        ));
                      } else {
                        return (
                          <div className="news-widget-item">
                            <p style={{ color: '#6B7280', fontSize: '14px', margin: 0 }}>No recent posts available.</p>
                          </div>
                        );
                      }
                    })()}
                  </div>
                </div>
                </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default BlogDetail;

